import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box,
  useToast 
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { transformData } from '../../../util/helper';
import { getPersonalityEducationData, updatePersonalityEducationData } from '../../../services/Personality';
import { RjsfSubmitButton } from '../../../RJSFCommonSchemas';
import FormLoader from '../../../CommonComponents/FormLoader';
import { personalityFormPlaceholders } from '../../../constants/constants';
import { showErrorToast, showInfoToast, showSuccessToast } from "../../../util/errorHandler";


const Education = () => {
  const { alias } = useParams();
  const [processedData, setProcessedData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const toast = useToast();


  const schema =  {
  "title": "Personality Education Fields",
  "type": "object",
  "properties": {
    "education": {
      "title": "Education",
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "year": { "type": "string", "title": "Year" },
          "education": { "type": "string", "title": "Institution/School Name" },
          "image_alias": { "type": "string", "title": "Image Alias" },
          "alias": { "type": "string", "title": "URL Alias" }
        },
        "required": ["year", "education", "alias"]
      }
    }
  }
  }

  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    "education": { 
      "ui:emptyValue": "",
      "items": {
        "year": {
          "ui:placeholder": personalityFormPlaceholders.EDUCATION_YEAR
        },
        "education": {
          "ui:placeholder": personalityFormPlaceholders.EDUCATION_INSTITUTION
        },
        "image_alias": {
          "ui:placeholder": personalityFormPlaceholders.EDUCATION_IMAGE_ALIAS
        },
        "alias": {
          "ui:placeholder": personalityFormPlaceholders.EDUCATION_URL_ALIAS
        }
      }
    }
  };

  const formData = processedData;

  // Function to identify changed items
  const getChangedItems = (currentData, originalData) => {
    if (!currentData.education || !originalData.education) return [];
    
    const changedItems = [];
    const currentEducation = currentData.education;
    const originalEducation = originalData.education;

    // Create a map of original items by id for efficient lookup
    const originalMap = new Map();
    originalEducation.forEach(item => {
      if (item.id) {
        originalMap.set(item.id, item);
      }
    });

    // Check current items against original
    currentEducation.forEach(currentItem => {
      if (currentItem.id && originalMap.has(currentItem.id)) {
        // Item exists in original - check if it's been updated
        const originalItem = originalMap.get(currentItem.id);
        
        // Compare each field individually
        const hasChanges = Object.keys(currentItem).some(key => {
          return currentItem[key] !== originalItem[key];
        });
        
        if (hasChanges) {
          // This is an updated item
          changedItems.push({
            ...currentItem,
            type: 'update'
          });
        }
      } else {
        // No id or not in original - this is a new item
        changedItems.push({
          ...currentItem,
          type: 'add'
        });
      }
    });

    // Check for deleted items (items that exist in original but not in current)
    originalEducation.forEach(originalItem => {
      const existsInCurrent = currentEducation.some(currentItem => currentItem.id === originalItem.id);
      
      if (!existsInCurrent) {
        // This item doesn't exist in current data - it's deleted
        changedItems.push({
          ...originalItem,
          type: 'delete'
        });
      }
    });
    
    return changedItems;
  };

  // Fetch initial data
  useEffect(() => {
    const getPrefaceData = async () => {
      try {
        if (alias) {
          const result = await getPersonalityEducationData(alias);
          if (result) {

            const fieldsToTransform = [
              'poster_image_carousel_alias', 
              'other_publicity_image_carousel_alias'
            ];

            const transformedData = transformData(result, fieldsToTransform);
            setProcessedData(transformedData);
            setOriginalData(transformedData); // Store original data for comparison
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
                showErrorToast(toast, "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };

    getPrefaceData();
  }, [alias, toast, quickSave]);

  // Submit updated data
  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    
    try {
      if (alias) {
        // Get only the changed items
        const changedItems = getChangedItems(formData, originalData);
        
        if (changedItems.length > 0) {
          // Send only the changed items
          const payload = { education: changedItems };
          
          const result = await updatePersonalityEducationData(alias, payload);
          if (result?.status === 200) {
            // Don't update originalData here - let it stay as the baseline
            // Only update processedData to reflect the current form state
            setProcessedData(formData);
            setQuickSave(prev => !prev);
                        showSuccessToast(toast, "Personalia data updated successfully");
          }
        } else {
          // No changes detected
                    showInfoToast(toast, "No changes detected");
        }
      }
    } catch (error) {
      console.error('Error updating education data:', error);
            showErrorToast(toast, "Failed to update data");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="1200px">
      {isLoading ? (
        <FormLoader />
      ) : (
          <Form schema={schema} uiSchema={uiSchema} formData={formData} validator={validator} onSubmit={(e) => handleSubmit(e.formData)} />
      )}
    </Box>
  );
};

export default Education;